/**
 * Operate → Requests — Engineering Intelligence intake workbench.
 * Request → Engineering Package → (approval) → Mission.
 * Not a chatbot, ticket board, or mission workspace.
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  useTheme,
} from '@mui/material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { TYPE } from '../../../design-system/foundation';
import { ErrorState, LoadingState, Page } from '../../../design-system/v2';
import {
  operateEngineeringRequestPath,
  operateExecutionsContextPath,
  operateTabPath,
} from '../workspace/projectPhases';
import { sentenceCase } from './operateStatus';
import {
  attachEngineeringRequestRepoURL,
  type EngineeringPackage,
  type EngineeringRequest,
  type EngineeringRequestLifecycleStep,
  requestIdOf,
  uploadEngineeringRequestEvidence,
  useAdvanceEngineeringRequest,
  useAnswerEngineeringClarification,
  useApproveEngineeringPackage,
  useEngineeringRequest,
  useEngineeringRequests,
  useRejectEngineeringPackage,
  useSubmitEngineeringRequest,
} from '../../../services/engineeringRequests';
import { useProjectCapabilities } from '../../../services/projectTeam';
import './requestsTab.css';
import { EnsMark } from '../../common/EnsLoader';

/** Request statuses reached once the mission has moved past Engineering
 * review into execution (internal/engineering/request/types.go StatusOrder,
 * tail end) — from here "Open Engineering" should land on Executions,
 * where the run's live progress actually shows, not the review workspace. */
const EXECUTING_REQUEST_STATUSES = new Set(['executing', 'validated', 'completed']);

const ACCEPT =
  '.pdf,.docx,.pptx,.xlsx,.xls,.md,.markdown,.txt,.eml,.msg,.png,.jpg,.jpeg,.gif,.webp,.zip,.tar,.gz,image/*,application/pdf,application/zip';

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

function confidencePct(c: number): number {
  if (c <= 1) return Math.round(c * 100);
  return Math.round(c);
}

function intentOf(r: EngineeringRequest): string {
  return r.engineering_intent || r.intent_text || '';
}

function uniqLabels(values: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of values) {
    const trimmed = String(raw || '').trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(trimmed);
  }
  return out;
}

function normalizeText(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, ' ');
}

function nearDuplicateText(a?: string | null, b?: string | null): boolean {
  const na = normalizeText(a || '');
  const nb = normalizeText(b || '');
  if (!na || !nb) return false;
  if (na === nb) return true;
  if (na.length >= 32 && (na.includes(nb) || nb.includes(na))) return true;
  return na.slice(0, 96) === nb.slice(0, 96);
}

function isSparseArchitecture(text?: string | null): boolean {
  if (!text?.trim()) return true;
  return /0 knowledge nodes\s*\/\s*0 edges/i.test(text) || /maps 0 knowledge nodes and 0 edges/i.test(text);
}

function packageImpactNarrative(pkg: EngineeringPackage): string {
  const apps = uniqLabels(pkg.affected_applications || []);
  const services = uniqLabels(pkg.affected_services || []).filter(
    (s) => !apps.some((a) => a.toLowerCase() === s.toLowerCase()),
  );
  const repos = uniqLabels(pkg.affected_repositories || []);
  const apis = uniqLabels(pkg.affected_apis || []);
  const infra = uniqLabels(pkg.affected_infrastructure || []);
  const techLine = infra.find((i) => /^technologies:/i.test(i));
  const otherInfra = infra.filter((i) => !/^technologies:/i.test(i));

  const parts: string[] = [];
  const base = (pkg.impact_analysis || '').trim();
  if (base) parts.push(base);

  const named: string[] = [];
  if (apps.length) named.push(`applications (${apps.slice(0, 4).join(', ')})`);
  if (services.length) named.push(`services (${services.slice(0, 4).join(', ')})`);
  if (apis.length) named.push(apis.slice(0, 3).join(', '));
  if (repos.length) named.push(`${repos.length} linked repositor${repos.length === 1 ? 'y' : 'ies'}`);
  if (otherInfra.length) named.push(otherInfra.slice(0, 3).join('; '));

  if (named.length) {
    const extra = `Focus the change on ${named.join(' · ')} — prefer in-place edits over introducing parallel stacks.`;
    if (!parts.some((p) => nearDuplicateText(p, extra))) parts.push(extra);
  }
  if (techLine) {
    const cleaned = techLine.replace(/^technologies:\s*/i, '').trim();
    if (cleaned) {
      const tech = `Detected stack signals: ${cleaned}.`;
      if (!parts.some((p) => nearDuplicateText(p, tech) || p.toLowerCase().includes(cleaned.toLowerCase().slice(0, 24)))) {
        parts.push(tech);
      }
    }
  }
  return parts.join(' ') || 'Impact analysis is still thin — confirm touchpoints before approval.';
}

const REQUEST_STATUS_ORDER = [
  'submitted',
  'understanding',
  'collecting_evidence',
  'clarification_required',
  'engineering_package_ready',
  'investigating',
  'awaiting_approval',
  'mission_created',
  'executing',
  'validated',
  'completed',
] as const;

function statusOrderIndex(stage: string): number {
  return REQUEST_STATUS_ORDER.indexOf(stage as (typeof REQUEST_STATUS_ORDER)[number]);
}

/** Normalize API lifecycle so terminal progress never pulses an earlier stage. */
function normalizeLifecycleSteps(
  steps: EngineeringRequestLifecycleStep[] | undefined,
  requestStatus?: string,
): Array<{ id: string; label: string; status: 'completed' | 'active' | 'pending' | 'idle' }> {
  if (!steps?.length) return [];

  let tip = requestStatus || '';
  let tipIdx = statusOrderIndex(tip);
  for (const s of steps) {
    const idx = statusOrderIndex(s.stage);
    if (idx > tipIdx) {
      tip = s.stage;
      tipIdx = idx;
    }
  }
  if (!tip) {
    const last = steps[steps.length - 1];
    tip = last.stage;
    tipIdx = statusOrderIndex(tip);
  }

  const terminal = tip === 'completed' || tip === 'rejected';
  // Upcoming stages stay visible as pending; a terminal tip drops what was never reached.
  const filtered = tipIdx < 0 || !terminal ? steps : steps.filter((s) => {
    const idx = statusOrderIndex(s.stage);
    return idx < 0 || idx <= tipIdx;
  });

  return filtered.map((s, i) => {
    const idx = statusOrderIndex(s.stage);
    let status: 'completed' | 'active' | 'pending' | 'idle';
    if (terminal || (idx >= 0 && tipIdx >= 0 && idx < tipIdx)) {
      status = 'completed';
    } else if (s.stage === tip) {
      status = terminal ? 'completed' : 'active';
    } else if (idx >= 0 && tipIdx >= 0 && idx > tipIdx) {
      status = 'pending';
    } else {
      status = LIFECYCLE_STATUS_MAP[s.status] ?? 'pending';
    }
    return { id: `${s.stage}-${i}`, label: s.label, status };
  });
}

/** Request lifecycle stages, in order. */
const REQUEST_STAGES = ['Submitted', 'Mission', 'Engineering', 'Testing', 'Execution'] as const;

/** Request status → index of the stage it is working on; past the last index means every stage is done. */
const STAGE_OF_STATUS: Record<string, number> = {
  submitted: 0,
  understanding: 1,
  collecting_evidence: 1,
  clarification_required: 1,
  engineering_package_ready: 1,
  investigating: 1,
  awaiting_approval: 1,
  mission_created: 2,
  executing: 3,
  validated: 4,
  completed: REQUEST_STAGES.length,
};

/** Furthest status the request has reached, from its status and API lifecycle. */
function requestTip(steps: EngineeringRequestLifecycleStep[] | undefined, requestStatus?: string): string {
  let tip = requestStatus || '';
  let tipIdx = statusOrderIndex(tip);
  for (const s of steps || []) {
    const idx = statusOrderIndex(s.stage);
    if (idx > tipIdx) {
      tip = s.stage;
      tipIdx = idx;
    }
  }
  return tip;
}

function requestLifecycleStages(
  steps: EngineeringRequestLifecycleStep[] | undefined,
  requestStatus?: string,
): Array<{ id: string; label: string; status: 'completed' | 'active' | 'pending' | 'idle' }> {
  const tip = requestTip(steps, requestStatus);
  const rejected = requestStatus === 'rejected';
  // A rejected request stops at the furthest stage its lifecycle reached.
  const reached = rejected
    ? Math.max(0, ...(steps || []).filter((s) => s.status === 'done' || s.status === 'active').map((s) => STAGE_OF_STATUS[s.stage] ?? 0))
    : STAGE_OF_STATUS[tip] ?? 0;
  return REQUEST_STAGES.map((label, i) => ({
    id: label,
    label,
    status: i < reached ? 'completed' : i === reached ? (rejected ? 'idle' : 'active') : rejected ? 'idle' : 'pending',
  }));
}

function packageUnderstandingNarrative(
  pkg: EngineeringPackage,
  requestIntent: string,
  reasoning?: string,
): string | null {
  const candidates = [pkg.understanding, reasoning].map((s) => (s || '').trim()).filter(Boolean);
  for (const text of candidates) {
    if (!nearDuplicateText(text, requestIntent) && !nearDuplicateText(text, pkg.engineering_intent)) {
      return text;
    }
  }
  // Synthesize when backend only echoed the intent.
  const kind = (pkg.request_type || 'engineering').replace(/_/g, ' ');
  const apps = uniqLabels(pkg.affected_applications || []).slice(0, 3);
  const scope = apps.length ? ` against ${apps.join(', ')}` : '';
  return `Interpreted as a ${kind} request${scope}. Keep the delivery surgical: change only what the intent requires and leave surrounding flows intact.`;
}


const LIFECYCLE_STATUS_MAP: Record<string, 'completed' | 'active' | 'pending' | 'idle'> = {
  done: 'completed',
  active: 'active',
  pending: 'pending',
  skipped: 'idle',
};

/** "TypeScript(494), Markdown(60)" from the technologies infra line → name/count pairs. */
function packageStackSignals(pkg: EngineeringPackage): { name: string; count?: string }[] {
  const line = (pkg.affected_infrastructure || []).find((i) => /^technologies:/i.test(i));
  if (!line) return [];
  return line
    .replace(/^technologies:\s*/i, '')
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean)
    .map((t) => {
      const m = /^(.*?)\s*\((\d+)\)$/.exec(t);
      return m ? { name: m[1], count: m[2] } : { name: t };
    });
}

const BULLET_RE = /^\s*(?:[-*•]|\d+[.)])\s+/;

/** Free-text plan/validation → paragraphs and bullet lists, as the API wrote them. */
const RichText: React.FC<{ text: string }> = ({ text }) => {
  const blocks: Array<{ kind: 'p' | 'ul'; lines: string[] }> = [];
  for (const raw of text.split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    const bullet = BULLET_RE.test(line);
    const kind = bullet ? 'ul' : 'p';
    const content = bullet ? line.replace(BULLET_RE, '') : line;
    const last = blocks[blocks.length - 1];
    if (kind === 'ul' && last?.kind === 'ul') last.lines.push(content);
    else blocks.push({ kind, lines: [content] });
  }
  return (
    <>
      {blocks.map((b, i) =>
        b.kind === 'ul' ? (
          <ul key={i}>
            {b.lines.map((l, j) => (
              <li key={j}>{l}</li>
            ))}
          </ul>
        ) : (
          <p key={i}>{b.lines[0]}</p>
        ),
      )}
    </>
  );
};

type LifecycleSeg = ReturnType<typeof normalizeLifecycleSteps>[number];

const LifecyclePanel: React.FC<{ steps: LifecycleSeg[] }> = ({ steps }) => {
  if (!steps.length) return null;
  const now = steps.find((s) => s.status === 'active');
  const allDone = steps.every((s) => s.status === 'completed');
  return (
    <section className="panel violet" aria-labelledby="rq-lc-h">
      <div className="p-head">
        <h3 id="rq-lc-h">Request lifecycle</h3>
        {now ? (
          <span className="sp lg cyan"><i />{now.label}</span>
        ) : allDone ? (
          <span className="sp lg ok"><i />Complete</span>
        ) : null}
      </div>
      <ol className="segs" aria-label="Stages" style={{ '--n': steps.length } as React.CSSProperties}>
        {steps.map((s) => (
          <li
            key={s.id}
            className={`sg ${s.status === 'completed' ? 'done' : s.status === 'active' ? 'now' : s.status}`}
            title={s.label}
            aria-current={s.status === 'active' ? 'step' : undefined}
          >
            <span className="bar" />
            <span className="nm">{s.label}</span>
            <span className="cp">
              {s.status === 'completed' ? 'Done' : s.status === 'active' ? 'In progress' : s.status === 'idle' ? 'Skipped' : 'Pending'}
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
};

type PkgTab = 'plan' | 'scope' | 'dec' | 'ver';

const PackagePanel: React.FC<{
  pkg: EngineeringPackage;
  status: string;
  busy?: boolean;
  canApprove?: boolean;
  onApprove: () => void;
  onReject: () => void;
  requestIntent: string;
  reasoning?: string;
  evidence: NonNullable<EngineeringRequest['evidence']>;
}> = ({ pkg, status, busy, canApprove = true, onApprove, onReject, requestIntent, reasoning, evidence }) => {
  const [confirmReject, setConfirmReject] = useState(false);
  const [tab, setTab] = useState<PkgTab>('plan');
  const awaiting = status === 'engineering_package_ready' || status === 'awaiting_approval';
  const approved = Boolean(pkg.approved_at) || status === 'mission_created';

  const apps = uniqLabels(pkg.affected_applications || []);
  const services = uniqLabels(pkg.affected_services || []).filter(
    (s) => !apps.some((a) => a.toLowerCase() === s.toLowerCase()),
  );
  const repos = uniqLabels(pkg.affected_repositories || []);
  const apis = uniqLabels(pkg.affected_apis || []);
  const understanding = packageUnderstandingNarrative(pkg, requestIntent, reasoning);
  const impact = (pkg.impact_analysis || '').trim() || packageImpactNarrative(pkg);
  const stack = packageStackSignals(pkg);
  const plan = (pkg.proposed_engineering_plan || '').trim();
  const validation = (pkg.validation_strategy || '').trim();
  const architecture =
    !isSparseArchitecture(pkg.architecture_analysis) ? (pkg.architecture_analysis || '').trim() : '';
  const defaults = (pkg.engineering_defaults || []).filter((d) => (d.decision || d.default || '').trim());
  const showApproval = awaiting && !approved && canApprove;

  const tabs: Array<{ id: PkgTab; label: string; count?: number }> = [
    { id: 'plan', label: 'Proposed plan' },
    { id: 'scope', label: 'Scope & impact' },
    { id: 'dec', label: 'Decisions', count: defaults.length },
    { id: 'ver', label: 'Verification' },
  ];

  const onTabKey = (e: React.KeyboardEvent<HTMLButtonElement>, i: number) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const next = tabs[(i + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
    setTab(next.id);
    document.getElementById(`rq-tab-${next.id}`)?.focus();
  };

  return (
    <section className="panel eng" aria-labelledby="rq-pk-h">
      <div className="p-head">
        <h3 id="rq-pk-h">Engineering package</h3>
        <span className="sp engine">Engineering Intelligence</span>
        {approved ? (
          <span className="sp ok"><i />Approved</span>
        ) : awaiting ? (
          <span className="sp ok"><i />Package ready</span>
        ) : (
          <span className="sp plain"><i />{sentenceCase(status)}</span>
        )}
        <span className="sp plain">Confidence {confidencePct(pkg.confidence)}%</span>
      </div>
      <p className="note">
        How Engineering Intelligence understood the request, what it will change, and how the result will be verified.
      </p>

      {awaiting && !approved && !canApprove ? (
        <div className="alert info"><span>Lead or admin approval required.</span></div>
      ) : null}
      {showApproval ? (
        <div className="row-actions">
          <button className="btn primary" type="button" disabled={busy} onClick={onApprove}>
            Approve package
          </button>
          <button className="btn" type="button" disabled={busy} onClick={() => setConfirmReject(true)}>
            Reject
          </button>
        </div>
      ) : null}

      <div className="tabs" role="tablist" aria-label="Package sections">
        {tabs.map((t, i) => (
          <button
            key={t.id}
            className="tab"
            type="button"
            role="tab"
            id={`rq-tab-${t.id}`}
            aria-controls={`rq-pn-${t.id}`}
            aria-selected={tab === t.id}
            tabIndex={tab === t.id ? 0 : -1}
            onClick={() => setTab(t.id)}
            onKeyDown={(e) => onTabKey(e, i)}
          >
            {t.label}
            {t.count ? <span className="ct">{t.count}</span> : null}
          </button>
        ))}
      </div>

      <div className="tabpanel" role="tabpanel" id={`rq-pn-${tab}`} aria-labelledby={`rq-tab-${tab}`}>
        {tab === 'plan' ? (
          plan ? (
            <div className="phase"><RichText text={plan} /></div>
          ) : (
            <div className="empty-tab">No plan was proposed for this package.</div>
          )
        ) : null}

        {tab === 'scope' ? (
          <>
            {understanding ? (
              <div className="phase">
                <span className="k">Interpretation</span>
                <p className="interp">{understanding}</p>
              </div>
            ) : null}
            <div className="phase">
              <span className="k">Impact analysis</span>
              <p className="interp">{impact}</p>
            </div>
            {apps.length || services.length || repos.length || apis.length ? (
              <div className="two" style={{ marginTop: 0 }}>
                {apps.length ? (
                  <div className="cell">
                    <span className="k">Applications · {apps.length}</span>
                    <div className="chips">{apps.map((a) => <span key={a} className="chip app">{a}</span>)}</div>
                  </div>
                ) : null}
                {services.length ? (
                  <div className="cell">
                    <span className="k">Services · {services.length}</span>
                    <div className="chips">{services.map((s) => <span key={s} className="chip mono">{s}</span>)}</div>
                  </div>
                ) : null}
                {repos.length ? (
                  <div className="cell">
                    <span className="k">Repositories · {repos.length}</span>
                    <div className="chips">{repos.map((s) => <code key={s}>{s}</code>)}</div>
                  </div>
                ) : null}
                {apis.length ? (
                  <div className="cell">
                    <span className="k">APIs · {apis.length}</span>
                    <div className="chips">{apis.map((s) => <code key={s}>{s}</code>)}</div>
                  </div>
                ) : null}
              </div>
            ) : null}
            {stack.length ? (
              <div className="cell">
                <span className="k">Stack signals</span>
                <div className="chips">
                  {stack.map((t) => (
                    <span key={t.name} className="chip">
                      {t.name}
                      {t.count ? <small>{t.count}</small> : null}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
            {architecture ? (
              <div className="phase">
                <span className="k">Architecture context</span>
                <p className="interp">{architecture}</p>
              </div>
            ) : null}
          </>
        ) : null}

        {tab === 'dec' ? (
          defaults.length ? (
            <>
              <p className="note" style={{ margin: 0 }}>
                Choices Engineering Intelligence made where the request left room.
              </p>
              <ul className="rows">
                {defaults.map((d) => (
                  <li key={`${d.decision}-${d.default}`} className="dec">
                    <span>{d.decision || d.topic}</span>
                    <div>
                      <b>{d.default}</b>
                      {d.rationale ? <small>{d.rationale}</small> : null}
                    </div>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <div className="empty-tab">No defaults were decided on your behalf.</div>
          )
        ) : null}

        {tab === 'ver' ? (
          validation ? (
            <div className="phase"><RichText text={validation} /></div>
          ) : (
            <div className="empty-tab">No verification strategy was recorded.</div>
          )
        ) : null}
      </div>

      {evidence.length ? (
        <div className="evidence-foot">
          <span>Evidence · {evidence.length}</span>
          {evidence.map((e) => (
            <span key={e.id} className="chip mono" title={e.name}>
              {e.kind} · {e.name}
            </span>
          ))}
        </div>
      ) : null}

      <Dialog
        open={confirmReject}
        onClose={() => setConfirmReject(false)}
        aria-labelledby="reject-package-title"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="reject-package-title" sx={{ ...TYPE.sectionTitle }}>
          Reject this package?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ ...TYPE.bodySm }}>
            The request stays on record, but no mission is created from this package.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="text" onClick={() => setConfirmReject(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={busy}
            onClick={() => {
              setConfirmReject(false);
              onReject();
            }}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </section>
  );
};

const RqIcon = {
  spark: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" /><circle cx="12" cy="12" r="2.5" /></svg>,
  link: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 14a4.5 4.5 0 0 0 6.4 0l3-3a4.5 4.5 0 0 0-6.4-6.4l-1 1" /><path d="M14 10a4.5 4.5 0 0 0-6.4 0l-3 3a4.5 4.5 0 0 0 6.4 6.4l1-1" /></svg>,
  clip: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m20 11.5-8.2 8.2a5 5 0 0 1-7.1-7.1l8.6-8.6a3.4 3.4 0 0 1 4.8 4.8l-8.5 8.5a1.7 1.7 0 0 1-2.4-2.4l7.8-7.8" /></svg>,
  arrow: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>,
  upload: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 15V4M7 9l5-5 5 5M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" /></svg>,
  file: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z" /><path d="M14 3v5h5" /></svg>,
  x: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>,
  list: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M4 7h16M4 12h10M4 17h7" /><path d="M18 15v6M15 18h6" /></svg>,
  panel: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><rect x="3.5" y="4.5" width="17" height="15" rx="2.5" /><path d="M9.5 4.5v15M13 9h4.5M13 12.5h4.5" /></svg>,
  search: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="11" cy="11" r="6.5" /><path d="m20 20-4-4" /></svg>,
  clipboard: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" /><rect x="9" y="3" width="6" height="4" rx="1.2" /><path d="M9 12h6M9 16h4" /></svg>,
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>,
  out: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 17 17 7M9 7h8v8" /></svg>,
};

const MAX_PENDING_FILES = 20;
const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

function fileSize(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1048576).toFixed(1)} MB`;
}

const RequestComposer = React.forwardRef<
  HTMLTextAreaElement,
  { projectId: string; onCreated: (r: EngineeringRequest) => void }
>(({ projectId, onCreated }, textRef) => {
  const submit = useSubmitEngineeringRequest(projectId);
  const advance = useAdvanceEngineeringRequest(projectId);
  const [intent, setIntent] = useState('');
  const [repoUrl, setRepoUrl] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [dragDepth, setDragDepth] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const addFiles = useCallback((files: FileList | null) => {
    if (!files?.length) return;
    setPendingFiles((prev) => [...prev, ...Array.from(files)].slice(0, MAX_PENDING_FILES));
  }, []);

  const canSubmit = Boolean(intent.trim()) && !submit.isPending;

  const onSubmit = async () => {
    const text = intent.trim();
    if (!text || submit.isPending) return;
    const urls = repoUrl.trim() ? [repoUrl.trim()] : [];
    let created: EngineeringRequest;
    try {
      created = await submit.mutateAsync({
        engineering_intent: text,
        repository_urls: urls,
      });
    } catch {
      // Surfaced below via submit.isError / submit.error.
      return;
    }
    const id = requestIdOf(created);
    for (const file of pendingFiles) {
      try {
        created = await uploadEngineeringRequestEvidence(projectId, id, file, false);
      } catch {
        /* best-effort */
      }
    }
    if (pendingFiles.length) {
      try {
        created = await advance.mutateAsync(id);
      } catch {
        /* already advanced on create */
      }
    }
    setIntent('');
    setRepoUrl('');
    setPendingFiles([]);
    onCreated(created);
  };

  const hasFiles = (e: React.DragEvent) => Array.from(e.dataTransfer?.types || []).includes('Files');

  return (
    <section
      className="card edge composer"
      aria-labelledby="rq-new-title"
      onDragEnter={(e) => {
        if (!hasFiles(e)) return;
        e.preventDefault();
        setDragDepth((d) => d + 1);
      }}
      onDragOver={(e) => {
        if (hasFiles(e)) e.preventDefault();
      }}
      onDragLeave={() => setDragDepth((d) => Math.max(0, d - 1))}
      onDrop={(e) => {
        if (!hasFiles(e)) return;
        e.preventDefault();
        setDragDepth(0);
        addFiles(e.dataTransfer.files);
      }}
    >
      <div className="c-head">
        <span className="c-icon" aria-hidden>{RqIcon.spark}</span>
        <div>
          <h2 id="rq-new-title">New request</h2>
          <p>Describe the engineering outcome. Drop documents here or add a repository to sharpen the package.</p>
        </div>
      </div>

      <div className="box">
        <label className="sr" htmlFor="rq-desc">Engineering intent</label>
        <textarea
          id="rq-desc"
          ref={textRef}
          placeholder="Describe the engineering outcome…"
          value={intent}
          onChange={(e) => setIntent(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && canSubmit) {
              e.preventDefault();
              void onSubmit();
            }
          }}
        />
        {pendingFiles.length > 0 ? (
          <div className="evidence" aria-label="Attached evidence">
            {pendingFiles.map((f, i) => (
              <span className="ev" key={`${f.name}-${f.size}-${i}`} title={f.name}>
                {RqIcon.file}
                <span className="name">{f.name}</span>
                <span className="size">{fileSize(f.size)}</span>
                <button
                  type="button"
                  aria-label={`Remove ${f.name}`}
                  onClick={() => setPendingFiles((prev) => prev.filter((_, j) => j !== i))}
                >
                  {RqIcon.x}
                </button>
              </span>
            ))}
          </div>
        ) : null}
        <div className="toolbar">
          <label className="repo">
            {RqIcon.link}
            <span className="sr">Repository URL</span>
            <input
              type="url"
              placeholder="Repository URL (optional)"
              autoComplete="off"
              spellCheck={false}
              value={repoUrl}
              onChange={(e) => setRepoUrl(e.target.value)}
            />
          </label>
          <button className="btn ghost" type="button" onClick={() => inputRef.current?.click()}>
            {RqIcon.clip}
            Add evidence
          </button>
          <input
            ref={inputRef}
            type="file"
            hidden
            multiple
            accept={ACCEPT}
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = '';
            }}
          />
          <button className="btn primary" type="button" disabled={!canSubmit} onClick={() => void onSubmit()}>
            Submit request
            {submit.isPending ? <EnsMark size="sm" icon={14} /> : RqIcon.arrow}
          </button>
        </div>
      </div>
      {submit.isError ? (
        <p className="err" role="alert">{errorText(submit.error, 'Couldn’t submit the request. Try again.')}</p>
      ) : (
        <p className="hint">
          Press <kbd>{IS_MAC ? '⌘' : 'Ctrl'}</kbd> + <kbd>Enter</kbd> to submit.
        </p>
      )}

      {dragDepth > 0 ? (
        <div className="drop" aria-hidden>
          <span>{RqIcon.upload}Drop files to add as evidence</span>
        </div>
      ) : null}
    </section>
  );
});
RequestComposer.displayName = 'RequestComposer';
function requestStatusLabel(r: EngineeringRequest): string {
  const tip = normalizeLifecycleSteps(r.lifecycle, r.status).find((s) => s.status === 'active');
  if (tip) return tip.label;
  if (r.status === 'completed') return 'Completed';
  return r.lifecycle?.find((s) => s.status === 'done')?.label || sentenceCase(r.status);
}

/** Pill tone for a request status — warning when it's waiting on a person. */
function statusTone(r: EngineeringRequest): string {
  if (needsClarification(r)) return 'warn';
  if (r.status === 'rejected') return 'plain';
  if (r.status === 'completed' || r.status === 'validated') return 'ok';
  if (r.mission_reference || statusOrderIndex(r.status) >= statusOrderIndex('mission_created')) return 'eng';
  return 'violet';
}

function needsClarification(r: EngineeringRequest): boolean {
  return r.status === 'clarification_required' || r.open_clarification?.status === 'open';
}

function shortTime(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const sameDay = d.toDateString() === new Date().toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function longTime(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const sameDay = d.toDateString() === new Date().toDateString();
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return sameDay ? `Submitted today, ${time}` : `Submitted ${d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}, ${time}`;
}

const RequestDetail: React.FC<{
  projectId: string;
  request: EngineeringRequest;
  onRefresh: () => void;
}> = ({ projectId, request, onRefresh }) => {
  const navigate = useNavigate();
  const rid = requestIdOf(request);
  const detailQ = useEngineeringRequest(projectId, rid);
  const advance = useAdvanceEngineeringRequest(projectId);
  const answerMut = useAnswerEngineeringClarification(projectId);
  const approveMut = useApproveEngineeringPackage(projectId);
  const rejectMut = useRejectEngineeringPackage(projectId);
  const { caps } = useProjectCapabilities(projectId);
  const canApprovePkg = !!caps?.can_approve;
  const [answer, setAnswer] = useState('');
  const [repoUrl, setRepoUrl] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [evidenceBusy, setEvidenceBusy] = useState(false);
  const [rawOpen, setRawOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();

  const r = detailQ.data || request;
  const id = requestIdOf(r);
  const open = r.open_clarification;
  const pkg = r.engineering_package;
  const understanding = (r.understanding?.summary as string) || '';
  const reasoning = (r.understanding?.reasoning as string) || '';
  // Set when the reasoning model could not evaluate the request.
  const evaluationError = typeof r.understanding?.evaluation_error === 'string' ? r.understanding.evaluation_error : '';
  const requestIntent = intentOf(r);
  const steps = requestLifecycleStages(r.lifecycle, r.status);
  const evidence = r.evidence || [];
  const clarifications = r.clarifications ?? [];
  const apps = uniqLabels(pkg?.affected_applications || []);
  const longIntent = requestIntent.length > 280 || requestIntent.split('\n').length > 4;

  /** Run a mutation; failures show an error alert instead of vanishing. */
  const runAction = async (fn: () => Promise<unknown>, fallback: string) => {
    setActionError(null);
    try {
      await fn();
      return true;
    } catch (err) {
      setActionError(errorText(err, fallback));
      return false;
    }
  };

  const onAttachFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    setEvidenceBusy(true);
    const ok = await runAction(async () => {
      for (const file of Array.from(files)) {
        await uploadEngineeringRequestEvidence(projectId, id, file, false);
      }
      await advance.mutateAsync(id);
    }, 'Couldn’t attach the evidence. Try again.');
    setEvidenceBusy(false);
    if (ok) {
      onRefresh();
      void qc.invalidateQueries({ queryKey: ['projects', projectId, 'engineering-requests', id] });
    }
  };

  const openEngineering = () =>
    navigate(
      EXECUTING_REQUEST_STATUSES.has(r.status)
        ? operateExecutionsContextPath(projectId, { kind: 'request', id })
        : r.mission_reference
          ? operateEngineeringRequestPath(projectId, id)
          : operateTabPath(projectId, 'engineering'),
    );

  const canAddEvidence = !pkg || r.status === 'collecting_evidence' || r.status === 'clarification_required';

  return (
    <>
      <div className="d-top">
        <div>
          <div className="d-sub">
            {r.request_type ? sentenceCase(r.request_type) : 'Engineering intent'}
            {r.created_at ? ` · ${longTime(r.created_at)}` : ''}
          </div>
          <h2 className="d-title">{requestIntent || r.title}</h2>
          <div className="d-meta">
            <span className={`sp lg ${statusTone(r)}`}><i />{requestStatusLabel(r)}</span>
            {!pkg ? <span className="sp lg plain">Confidence {confidencePct(r.confidence)}%</span> : null}
            {apps.length ? (
              <span className="sp lg plain">
                {apps.length} application{apps.length === 1 ? '' : 's'}
              </span>
            ) : null}
            {r.submitted_by ? <span className="sp lg plain">{r.submitted_by}</span> : null}
          </div>
        </div>
        {r.mission_reference ? (
          <div className="d-actions">
            <button className="btn primary" type="button" onClick={openEngineering}>
              {EXECUTING_REQUEST_STATUSES.has(r.status) ? 'View execution' : 'Open mission in Engineering'}
              {RqIcon.out}
            </button>
          </div>
        ) : null}
      </div>

      {actionError ? (
        <div className="alert err" role="alert">
          <span>{actionError}</span>
          <button className="lnk" type="button" onClick={() => setActionError(null)}>Dismiss</button>
        </div>
      ) : null}

      <LifecyclePanel steps={steps} />

      <section className="panel" aria-labelledby="rq-rq-h">
        <div className="p-head">
          <h3 id="rq-rq-h">What was asked</h3>
          {longIntent ? (
            <button
              className="lnk"
              type="button"
              aria-expanded={rawOpen}
              aria-controls="rq-raw"
              onClick={() => setRawOpen((v) => !v)}
            >
              {rawOpen ? 'Show less' : 'Show full request'}
            </button>
          ) : null}
        </div>
        <div className={`raw${longIntent && !rawOpen ? ' clamp' : ''}`} id="rq-raw">
          {requestIntent || r.title}
        </div>
        {understanding && !pkg && !nearDuplicateText(understanding, requestIntent) ? (
          <div className="cell" style={{ marginTop: 12 }}>
            <span className="k">Understanding</span>
            <p>{understanding}</p>
          </div>
        ) : null}
      </section>

      {open && open.status === 'open' ? (
        <section className="panel warn" aria-labelledby="rq-cl-h">
          <div className="p-head">
            <h3 id="rq-cl-h">Clarification required</h3>
            <span className="sp warn"><i />Needs you</span>
          </div>
          <p className="interp">{open.question}</p>
          <textarea
            className="answer"
            placeholder="Your answer"
            aria-label="Your answer"
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
          />
          <div className="row-actions">
            <button
              className="btn primary"
              type="button"
              disabled={!answer.trim() || answerMut.isPending}
              onClick={async () => {
                const ok = await runAction(
                  () =>
                    answerMut.mutateAsync({
                      requestId: id,
                      clarificationId: open.clarification_id,
                      answer: answer.trim(),
                    }),
                  'Couldn’t send your answer. Try again.',
                );
                if (ok) {
                  setAnswer('');
                  onRefresh();
                }
              }}
            >
              {answerMut.isPending ? 'Re-evaluating…' : 'Submit answer'}
            </button>
          </div>
        </section>
      ) : null}

      {evaluationError ? (
        <div className="alert err" role="alert">
          <span>{evaluationError} Nothing was decided or asked on its behalf.</span>
          <button
            className="btn"
            type="button"
            disabled={advance.isPending}
            onClick={async () => {
              if (await runAction(() => advance.mutateAsync(id), 'Couldn’t evaluate the request. Try again.')) {
                onRefresh();
              }
            }}
          >
            {advance.isPending ? 'Evaluating…' : 'Evaluate again'}
          </button>
        </div>
      ) : r.status === 'collecting_evidence' ? (
        <div className="alert info">
          <span>
            More engineering evidence is needed before a package can be sealed. Attach documents or a repository URL,
            then continue reasoning.
          </span>
        </div>
      ) : null}

      {pkg ? (
        <PackagePanel
          pkg={pkg}
          status={r.status}
          busy={approveMut.isPending || rejectMut.isPending}
          canApprove={canApprovePkg}
          requestIntent={requestIntent}
          reasoning={reasoning}
          evidence={evidence}
          onApprove={async () => {
            if (await runAction(() => approveMut.mutateAsync(id), 'Couldn’t approve the package. Try again.')) {
              onRefresh();
            }
          }}
          onReject={async () => {
            if (await runAction(() => rejectMut.mutateAsync({ requestId: id }), 'Couldn’t reject the package. Try again.')) {
              onRefresh();
            }
          }}
        />
      ) : null}

      {clarifications.length > 0 ? (
        <section className="panel" aria-labelledby="rq-cv-h">
          <div className="p-head">
            <h3 id="rq-cv-h">Conversation with the AI</h3>
            <span className="sp plain">{clarifications.length}</span>
          </div>
          <div className="rows" style={{ marginTop: 12 }}>
            {clarifications.map((c) => {
              const answered = !!c.answer;
              const hasDelta =
                typeof c.confidence_before === 'number' && typeof c.expected_confidence_after === 'number';
              return (
                <div key={c.clarification_id} className={`qa${answered ? '' : ' open'}`}>
                  <div className="qq">
                    <span>{c.question}</span>
                    {hasDelta ? (
                      <span
                        className={`delta${answered ? ' ok' : ''}`}
                        aria-label={`Confidence ${confidencePct(c.confidence_before as number)}% to ${confidencePct(c.expected_confidence_after as number)}%`}
                      >
                        {confidencePct(c.confidence_before as number)}% → <b>{confidencePct(c.expected_confidence_after as number)}%</b>
                      </span>
                    ) : null}
                  </div>
                  {c.missing_evidence ? (
                    <div className={`qm${answered ? ' struck' : ' warn'}`}>Missing: {c.missing_evidence}</div>
                  ) : null}
                  {c.answer ? (
                    <div className="qa-ans">{c.answer}</div>
                  ) : (
                    <div className="qm"><em>Awaiting your answer above.</em></div>
                  )}
                  {c.reasoning ? <div className="qm">Why the AI asked: {c.reasoning}</div> : null}
                </div>
              );
            })}
          </div>
        </section>
      ) : null}

      {!pkg || canAddEvidence ? (
        <section className="panel" aria-labelledby="rq-ev-h">
          <div className="p-head">
            <h3 id="rq-ev-h">Evidence</h3>
            <span className="sp plain">{evidence.length}</span>
          </div>
          {evidence.length ? (
            <div className="chips" style={{ marginTop: 12 }}>
              {evidence.map((e) => (
                <span key={e.id} className="chip mono" title={e.name}>
                  {e.kind} · {e.name}
                </span>
              ))}
            </div>
          ) : (
            <p className="note">No evidence attached yet.</p>
          )}
          {canAddEvidence ? (
            <div className="row-actions">
              <button className="btn ghost" type="button" disabled={evidenceBusy} onClick={() => fileRef.current?.click()}>
                {RqIcon.clip}
                {evidenceBusy ? 'Attaching…' : 'Attach evidence'}
              </button>
              <input
                ref={fileRef}
                hidden
                type="file"
                multiple
                accept={ACCEPT}
                onChange={(e) => {
                  void onAttachFiles(e.target.files);
                  e.target.value = '';
                }}
              />
              <label className="repo">
                {RqIcon.link}
                <span className="sr">Add repository URL</span>
                <input
                  type="url"
                  placeholder="Add repository URL"
                  autoComplete="off"
                  spellCheck={false}
                  value={repoUrl}
                  onChange={(e) => setRepoUrl(e.target.value)}
                />
              </label>
              <button
                className="btn"
                type="button"
                disabled={!repoUrl.trim() || advance.isPending}
                onClick={async () => {
                  const ok = await runAction(async () => {
                    await attachEngineeringRequestRepoURL(projectId, id, repoUrl.trim(), false);
                    await advance.mutateAsync(id);
                  }, 'Couldn’t add the repository URL. Try again.');
                  if (ok) {
                    setRepoUrl('');
                    onRefresh();
                  }
                }}
              >
                Add URL
              </button>
              <button
                className="btn primary"
                type="button"
                disabled={advance.isPending}
                onClick={async () => {
                  if (await runAction(() => advance.mutateAsync(id), 'Couldn’t continue reasoning. Try again.')) {
                    onRefresh();
                  }
                }}
              >
                Continue reasoning
              </button>
            </div>
          ) : null}
        </section>
      ) : null}
    </>
  );
};

const OperateRequestsTab: React.FC = () => {
  const { projectId = '' } = useParams();
  const [search, setSearch] = useSearchParams();
  const dark = useTheme().palette.mode === 'dark';
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const selectedId = search.get('request') || undefined;
  const listQ = useEngineeringRequests(projectId);
  const requests = useMemo(() => listQ.data || [], [listQ.data]);
  const [query, setQuery] = useState('');
  const [composerOpen, setComposerOpen] = useState(false);

  const filtered = useMemo(() => {
    const q = normalizeText(query);
    if (!q) return requests;
    return requests.filter((r) =>
      normalizeText(`${intentOf(r)} ${r.title} ${r.request_type || ''} ${requestStatusLabel(r)}`).includes(q),
    );
  }, [requests, query]);

  const stats = useMemo(() => {
    const today = new Date().toDateString();
    return {
      total: requests.length,
      today: requests.filter((r) => r.created_at && new Date(r.created_at).toDateString() === today).length,
      missions: requests.filter((r) => Boolean(r.mission_reference)).length,
      executing: requests.filter((r) => EXECUTING_REQUEST_STATUSES.has(r.status)).length,
      clarify: requests.filter(needsClarification).length,
    };
  }, [requests]);

  const selected = useMemo(() => {
    if (!requests.length) return undefined;
    if (selectedId) {
      return requests.find((r) => requestIdOf(r) === selectedId) || requests[0];
    }
    return requests[0];
  }, [requests, selectedId]);

  const select = (id: string) => {
    const next = new URLSearchParams(search);
    next.set('request', id);
    setSearch(next, { replace: true });
  };

  const showComposer = composerOpen || (!listQ.isLoading && !listQ.isError && requests.length === 0);

  const focusComposer = () => {
    setComposerOpen(true);
    requestAnimationFrame(() => {
      composerRef.current?.focus();
      composerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  };

  const onListKeyDown = (e: React.KeyboardEvent<HTMLUListElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const btns = Array.from(e.currentTarget.querySelectorAll<HTMLButtonElement>('.item'));
    const i = btns.indexOf(document.activeElement as HTMLButtonElement);
    if (i < 0) return;
    e.preventDefault();
    const next = btns[(i + (e.key === 'ArrowDown' ? 1 : btns.length - 1)) % btns.length];
    next.focus();
    next.click();
  };

  return (
    <Page>
      <div className={`rq-scope${dark ? ' rq-dark' : ''}`}>
        <section className="card edge" aria-labelledby="rq-h-title">
          <div className="head">
            <div className="tile-icon" aria-hidden>{RqIcon.clipboard}</div>
            <div className="h-id">
              <h1 id="rq-h-title">Engineering requests</h1>
              <div className="h-meta">Engineering intent turned into planned missions</div>
            </div>
            <button
              className="btn primary"
              type="button"
              aria-expanded={showComposer}
              onClick={() => (composerOpen && requests.length ? setComposerOpen(false) : focusComposer())}
            >
              {RqIcon.plus}
              {composerOpen && requests.length ? 'Close' : 'New request'}
            </button>
          </div>
          <div className="tiles">
            <div className="stat">
              <div className="lab">All requests</div>
              <div className="num">
                <b>{stats.total}</b>
                {stats.today ? <span className="sp violet"><i />{stats.today} submitted today</span> : null}
              </div>
            </div>
            <div className="stat t-eng">
              <div className="lab">Missions created</div>
              <div className="num">
                <b>{stats.missions}</b>
                {stats.executing ? <span className="sp eng"><i />{stats.executing} executing</span> : null}
              </div>
            </div>
            <div className={`stat ${stats.clarify ? 't-warn' : 't-ok'}`}>
              <div className="lab">Needs clarification</div>
              <div className="num">
                <b>{stats.clarify}</b>
                {stats.clarify ? (
                  <span className="sp warn"><i />Waiting on you</span>
                ) : (
                  <span className="sp ok"><i />Nothing waiting on you</span>
                )}
              </div>
            </div>
          </div>
        </section>

        {showComposer ? (
          <RequestComposer
            ref={composerRef}
            projectId={projectId}
            onCreated={(r) => {
              setComposerOpen(false);
              select(requestIdOf(r));
              void listQ.refetch();
            }}
          />
        ) : null}

        <section className="card edge ws" aria-label="Requests">
          <div className="q">
            <div className="q-head">
              <div className="q-title">
                <h2>All requests</h2>
                <span className="count-pill">{requests.length}</span>
              </div>
              <label className="field">
                <span className="sr">Search requests</span>
                {RqIcon.search}
                <input
                  type="search"
                  placeholder="Search requests…"
                  autoComplete="off"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </label>
            </div>
            {listQ.isError ? (
              <ErrorState title="Couldn’t load requests" error={listQ.error} onRetry={() => void listQ.refetch()} />
            ) : listQ.isLoading ? (
              <LoadingState variant="skeleton" rows={4} label="Loading requests…" />
            ) : requests.length === 0 ? (
              <div className="empty">
                <span className="e-icon" aria-hidden>{RqIcon.list}</span>
                <h3>No requests yet</h3>
                <p>Describe an engineering outcome above to create the first request.</p>
                <button className="btn ghost" type="button" onClick={focusComposer}>
                  Write a request
                </button>
              </div>
            ) : filtered.length === 0 ? (
              <p className="q-none">No requests match “{query}”.</p>
            ) : (
              <ul className="q-list" role="listbox" aria-label="Requests" onKeyDown={onListKeyDown}>
                {filtered.map((r) => {
                  const id = requestIdOf(r);
                  const active = Boolean(selected && requestIdOf(selected) === id);
                  return (
                    <li key={id}>
                      <button
                        type="button"
                        className="item"
                        role="option"
                        aria-selected={active}
                        tabIndex={active ? 0 : -1}
                        onClick={() => select(id)}
                      >
                        <span className="i-top">
                          <span>{r.request_type ? sentenceCase(r.request_type) : 'Engineering intent'}</span>
                          <span>{shortTime(r.created_at)}</span>
                        </span>
                        <span className="i-title">{intentOf(r) || r.title}</span>
                        <span className="i-meta">
                          <span className={`sp ${statusTone(r)}`}><i />{requestStatusLabel(r)}</span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="d" aria-live="polite">
            {selected ? (
              <RequestDetail
                key={requestIdOf(selected)}
                projectId={projectId}
                request={selected}
                onRefresh={() => {
                  void listQ.refetch();
                }}
              />
            ) : (
              <div className="empty">
                <span className="e-icon" aria-hidden>{RqIcon.panel}</span>
                <h3>No request selected</h3>
                <p>Pick a request from the list, or submit a new one.</p>
              </div>
            )}
          </div>
        </section>
      </div>
    </Page>
  );
};

export default OperateRequestsTab;
